import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DatabaseBackup, Download, RotateCcw, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, BASE, refreshSession } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button, Card, EmptyState, Loading, Page, PageHeader } from "@/components/kit";
import { BackupUpload } from "@/components/master/backup/BackupUpload";
import { RestoreDialog } from "@/components/master/backup/RestoreDialog";

const SOURCE = { manual: "Manual", automatico: "Automático", enviado: "Enviado", seguranca: "Segurança" };
const mb = (n) => `${((n || 0) / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;

async function download(b) {
  await refreshSession().catch(() => {});
  window.location.href = `${BASE}/master/platform-backups/${b.id}/download`;
}

function RestoreStatus({ r }) {
  if (!r) return null;
  const label = { restaurando: "Restaurando…", concluido: "Última restauração concluída", erro: "Última restauração falhou" }[r.status];
  return (
    <Card className="flex items-center gap-3 text-sm" data-testid="platform-restore-status">
      {r.status === "restaurando" ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
      <span className="flex-1"><b>{label}</b> · {r.mode === "full" ? "plataforma inteira" : r.tenants.join(", ")} · {fmtDateTime(r.created_at)}{r.error ? ` · ${r.error}` : ""}</span>
    </Card>
  );
}

function Row({ b, onRestore }) {
  const qc = useQueryClient();
  const del = useMutation({ mutationFn: () => api(`/master/platform-backups/${b.id}`, { method: "DELETE" }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["platform-backups"] }); toast.success("Backup excluído"); }, onError: (e) => toast.error(e.message) });
  const ready = b.status === "pronto";
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`platform-backup-row-${b.id}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="label">{fmtDateTime(b.created_at)} <span className="badge badge-brand ml-1">{SOURCE[b.source] || b.source}</span></div>
        <div className="caption">{b.status === "gerando" ? "Gerando…" : b.status === "erro" ? `Falhou · ${b.error || ""}` : `${mb(b.size)} · ${b.manifest?.tenants?.length || 0} restaurantes · ${b.manifest?.files || 0} arquivos`}</div>
      </div>
      {ready && <Button variant="secondary" size="sm" icon={Download} onClick={() => download(b)} data-testid={`platform-backup-download-${b.id}`}>Baixar</Button>}
      {ready && <Button variant="secondary" size="sm" icon={RotateCcw} onClick={() => onRestore(b)} data-testid={`platform-backup-restore-${b.id}`}>Restaurar</Button>}
      {b.status !== "gerando" && <button className="icon-btn" title="Excluir" onClick={() => window.confirm("Excluir este backup?") && del.mutate()} data-testid={`platform-backup-delete-${b.id}`}><Trash2 size={15} /></button>}
    </div>
  );
}

export default function PlatformBackup() {
  const qc = useQueryClient();
  const [restoring, setRestoring] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["platform-backups"], queryFn: () => api("/master/platform-backups"),
    refetchInterval: (q) => (q.state.data?.backups?.some((b) => b.status === "gerando") || q.state.data?.restore?.status === "restaurando" ? 3000 : false),
  });
  const make = useMutation({ mutationFn: () => api("/master/platform-backups", { method: "POST" }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["platform-backups"] }); toast.success("Gerando backup da plataforma…"); }, onError: (e) => toast.error(e.message) });
  const rows = data?.backups || [];
  return (
    <Page>
      <PageHeader title="Backup da plataforma" subtitle="Todos os restaurantes, usuários, configurações, fotos e arquivos · automático todo dia às 3h (7 últimos guardados)"
        right={<Button icon={DatabaseBackup} loading={make.isPending} onClick={() => make.mutate()} data-testid="platform-backup-create-button"><span className="sm:hidden">Gerar</span><span className="hidden sm:inline">Gerar backup agora</span></Button>} />
      <div className="flex flex-col gap-4">
        <RestoreStatus r={data?.restore} />
        <BackupUpload />
        {isLoading ? <Loading /> : !rows.length ? <EmptyState title="Nenhum backup ainda" text="Gere o primeiro backup ou envie um arquivo .zip." testid="platform-backup-empty" /> : (
          <div className="card divide" style={{ padding: 0 }} data-testid="platform-backup-list">{rows.map((b) => <Row key={b.id} b={b} onRestore={setRestoring} />)}</div>
        )}
      </div>
      <RestoreDialog key={restoring?.id} backup={restoring} onClose={() => setRestoring(null)} />
    </Page>
  );
}
